import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, AlertTriangle, Info, FileText, Download, Trash2 } from 'lucide-react';
import { downloadWithAuth } from '@/components/MesDonnees';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('fr-FR');
};
const fmtEvent = (iso) => (iso ? iso.replace('T', ' ').slice(0, 16) : '—');

const Tile = ({ label, value, tone = 'slate', testid }) => {
  const tones = {
    slate: 'bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100',
    green: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300',
    amber: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
    red: 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300',
  };
  return (
    <div className={`rounded-xl p-4 text-center ${tones[tone]}`} data-testid={testid}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs mt-1">{label}</div>
    </div>
  );
};

// Onglet RGPD de l'administration : état de la charte, comptes et données au-delà
// de leur durée de conservation (aucune suppression automatique : c'est
// l'administrateur qui décide, avec confirmation), registre des signatures.
const RgpdPanel = () => {
  const [etat, setEtat] = useState(null);
  const [registre, setRegistre] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, r] = await Promise.all([
        fetch(`${BACKEND_URL}/api/rgpd/etat`, { headers }),
        fetch(`${BACKEND_URL}/api/rgpd/charte/signatures`, { headers }),
      ]);
      if (e.ok) setEtat(await e.json());
      if (r.ok) setRegistre(await r.json());
    } catch {
      toast.error('Erreur de chargement du suivi RGPD');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const eraseAccount = async (acc) => {
    const answer = window.prompt(
      `Effacement définitif de ${acc.name}.\n\nSon compte, son profil, sa photo, ses documents et son journal de connexion seront supprimés. ` +
      "La main courante et les documents à valeur probante sont conservés sans son nom.\n\nIrréversible. Tapez SUPPRIMER pour confirmer."
    );
    if (answer !== 'SUPPRIMER') return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${acc.id}?confirmation=SUPPRIMER`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Données personnelles effacées'); load(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur de connexion'); }
  };

  const purgeAttachments = async () => {
    const ids = etat.pieces_jointes_a_supprimer.liste.map(e => e.id);
    const answer = window.prompt(
      `Supprimer les pièces jointes de ${ids.length} événement(s) conservés depuis plus de ${etat.pieces_jointes_a_supprimer.duree_mois} mois ?\n\n` +
      "Les événements et leur description sont conservés ; seuls les fichiers (photos, documents) sont supprimés, avec une trace dans l'historique de chaque événement. Irréversible.\n\nTapez SUPPRIMER pour confirmer."
    );
    if (answer !== 'SUPPRIMER') return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/rgpd/pieces-jointes/supprimer`, {
        method: 'POST', headers, body: JSON.stringify({ event_ids: ids, confirmation: 'SUPPRIMER' })
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`${d.fichiers} fichier(s) supprimé(s) sur ${d.evenements} événement(s)`);
        load();
      } else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur de connexion'); }
  };

  const proof = async (p) => {
    try { await downloadWithAuth(`/api/charte/preuves/${p.id}/pdf`, 'preuve_charte.pdf'); }
    catch { toast.error('Impossible de télécharger la preuve'); }
  };

  if (loading && !etat) return <p className="text-slate-500">Chargement…</p>;
  if (!etat) return <p className="text-rose-600">Suivi RGPD indisponible.</p>;

  const org = etat.organisation;
  const filtered = registre.filter(p => !search || `${p.nom} ${p.identifiant} ${p.version}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="rgpd-panel">
      {etat.alertes.map((a, i) => (
        <div key={i} className={`flex gap-2 rounded-lg p-3 text-sm border ${a.niveau === 'danger'
          ? 'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-950/40 dark:border-rose-900 dark:text-rose-200'
          : 'bg-sky-50 border-sky-200 text-sky-800 dark:bg-sky-950/40 dark:border-sky-900 dark:text-sky-200'}`} data-testid="rgpd-alerte">
          {a.niveau === 'danger' ? <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> : <Info className="h-4 w-4 shrink-0 mt-0.5" />}
          <span>{a.texte}</span>
        </div>
      ))}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label={`Charte signée (sur ${etat.comptes_actifs} comptes actifs)`} value={etat.charte_signee} tone="green" testid="tile-signee" />
        <Tile label="Charte à signer" value={etat.a_signer.length} tone={etat.a_signer.length ? 'amber' : 'slate'} testid="tile-a-signer" />
        <Tile label="Comptes désactivés" value={etat.comptes_desactives} testid="tile-desactives" />
        <Tile label={`Comptes à supprimer (> ${org.conservation_compte_mois} mois)`} value={etat.comptes_a_supprimer.length} tone={etat.comptes_a_supprimer.length ? 'red' : 'slate'} testid="tile-a-supprimer" />
      </div>

      <Card className="glass-effect">
        <CardHeader><CardTitle className="text-base">Responsable de traitement</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1">
          <p>{org.organisme} — SIRET {org.siret} — {org.adresse}</p>
          <p>Contact RGPD : {org.contact_rgpd} · Hébergeur : {org.hebergeur}</p>
          <p>Version de la charte en vigueur : <strong>{org.version}</strong>. Pour imposer une nouvelle signature à tous après une modification du texte, changez <code>CHARTE_VERSION</code> (backend/rgpd_utils.py).</p>
          <p><a className="underline text-indigo-600" href="/charte" target="_blank" rel="noreferrer">Voir la charte (page publique)</a></p>
        </CardContent>
      </Card>

      <Card className="glass-effect">
        <CardHeader><CardTitle className="text-base">Charte non signée ({etat.a_signer.length})</CardTitle></CardHeader>
        <CardContent>
          {etat.a_signer.length === 0 ? <p className="text-sm text-slate-500">Tous les comptes actifs ont signé la version en vigueur.</p> : (
            <ul className="text-sm divide-y">
              {etat.a_signer.map(a => (
                <li key={a.id} className="py-1.5 flex justify-between gap-2">
                  <span>{a.name} <span className="text-slate-400">({a.role})</span></span>
                  <span className="text-xs text-slate-500">{a.derniere_version ? `dernière version signée : ${a.derniere_version}` : 'jamais signée'}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="glass-effect">
        <CardHeader><CardTitle className="text-base">Comptes à supprimer ({etat.comptes_a_supprimer.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-slate-500">Comptes désactivés depuis plus de {org.conservation_compte_mois} mois. Rien n'est supprimé automatiquement : vous décidez, avec confirmation.</p>
          {etat.comptes_a_supprimer.length === 0 ? <p className="text-sm text-slate-500">Aucun compte concerné.</p> : (
            <ul className="text-sm divide-y">
              {etat.comptes_a_supprimer.map(a => (
                <li key={a.id} className="py-1.5 flex items-center justify-between gap-2">
                  <span>{a.name} <span className="text-slate-400">· désactivé le {fmtDate(a.desactive_le)}</span></span>
                  <span className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => downloadWithAuth(`/api/users/${a.id}/donnees`, 'donnees.json').catch(() => toast.error('Export impossible'))}>
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-rose-600" onClick={() => eraseAccount(a)} data-testid={`rgpd-erase-${a.id}`}>
                      <Trash2 className="h-4 w-4 mr-1" />Effacer
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="glass-effect">
        <CardHeader><CardTitle className="text-base">Pièces jointes à supprimer ({etat.pieces_jointes_a_supprimer.total})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-slate-500">
            Photos et fichiers d'événements de plus de {etat.pieces_jointes_a_supprimer.duree_mois} mois (tous types d'événements).
            L'événement et sa description restent ; seuls les fichiers sont supprimés.
          </p>
          {etat.pieces_jointes_a_supprimer.total === 0 ? <p className="text-sm text-slate-500">Aucune pièce jointe concernée.</p> : (
            <>
              <ul className="text-xs divide-y max-h-48 overflow-y-auto">
                {etat.pieces_jointes_a_supprimer.liste.map(e => (
                  <li key={e.id} className="py-1">{fmtEvent(e.event_datetime)} · {e.site_name} · {e.category} / {e.event_type} · {e.attachments.length} fichier(s)</li>
                ))}
              </ul>
              <Button variant="outline" className="text-rose-600" onClick={purgeAttachments} data-testid="rgpd-purge-pj">
                <Trash2 className="h-4 w-4 mr-1" />Supprimer ces pièces jointes
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="glass-effect">
        <CardHeader><CardTitle className="text-base">Entrées de main courante à examiner ({etat.evenements_a_examiner.total})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-slate-500">
            Événements de plus de {etat.evenements_a_examiner.duree_ans} ans (durée de prescription). Les entrées de main courante servent de preuve : elles ne sont jamais supprimées automatiquement
            ni depuis l'application ; leur suppression éventuelle, une fois la durée dépassée, reste une décision du responsable de traitement.
          </p>
          {etat.evenements_a_examiner.total === 0 ? <p className="text-sm text-slate-500">Aucune entrée n'a dépassé cette durée.</p> : (
            <ul className="text-xs divide-y max-h-48 overflow-y-auto">
              {etat.evenements_a_examiner.liste.map(e => (
                <li key={e.id} className="py-1">{fmtEvent(e.event_datetime)} · {e.site_name} · {e.category} / {e.event_type}</li>
              ))}
            </ul>
          )}
          {etat.preuves_charte_a_supprimer > 0 && (
            <p className="text-xs text-amber-700">{etat.preuves_charte_a_supprimer} preuve(s) de signature de comptes effacés depuis plus de {org.conservation_preuves_ans} ans peuvent être supprimées.</p>
          )}
        </CardContent>
      </Card>

      <Card className="glass-effect">
        <CardHeader>
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-indigo-600" /><CardTitle className="text-base">Registre des signatures de la charte ({registre.length})</CardTitle></div>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input placeholder="Rechercher un nom, un identifiant, une version…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="rgpd-search" />
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left" data-testid="rgpd-registre">
              <thead className="text-xs uppercase text-slate-500">
                <tr><th className="py-1 pr-3">Date</th><th className="pr-3">Nom</th><th className="pr-3">Version</th><th className="pr-3">IP</th><th className="pr-3">Intégrité</th><th /></tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} className="border-t">
                    <td className="py-1.5 pr-3 whitespace-nowrap">{fmtDate(p.date)}</td>
                    <td className="pr-3">{p.nom}<div className="text-xs text-slate-400">{p.identifiant}</div></td>
                    <td className="pr-3">{p.version}</td>
                    <td className="pr-3 text-xs">{p.ip}</td>
                    <td className="pr-3">{p.integre ? <Badge className="bg-emerald-600 hover:bg-emerald-600">Intègre</Badge> : <Badge className="bg-rose-600 hover:bg-rose-600">Altérée</Badge>}</td>
                    <td className="text-right"><Button size="sm" variant="outline" onClick={() => proof(p)}><FileText className="h-4 w-4 mr-1" />PDF</Button></td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={6} className="py-3 text-slate-500">Aucune signature.</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RgpdPanel;
